import { Injectable } from '@angular/core';
import { DataMiningDialogData } from '../modules/pages/hot-alert/cec-recovery-plan-dialog/data-mining-form-builder';
import { BehaviorSubject, Observable, catchError, from, map, of, switchMap, tap } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { ApplicationService } from './application.service';
import { MyUtils } from './my-utils.service';
import { CecRecoveryPlanDialogComponent } from '../modules/pages/hot-alert/cec-recovery-plan-dialog/cec-recovery-plan-dialog.component';
import { DataMiningAdapter } from '../modules/pages/hot-alert/configurations/data-mining-plan-adapter';
import { RecoveryPlan } from '../modules/pages/hot-alert/configurations/recovery-plan';
import { IApiService } from '../modules/records/models/api/i-api-service';
import { IPayload, Payload } from '../interface/filters';
import { RecoveryPlanInfoComponent } from '../modules/pages/hot-alert/recovery-plan-info/recovery-plan-info.component';
import { cloneDeep } from 'lodash';
import { genDataMiningTableConfig } from '../modules/pages/hot-alert/configurations/data-mining-table-config';

@Injectable({
  providedIn: 'root'
})
export class DataMiningService implements IApiService<RecoveryPlan>{
  endpoint = environment.csibefrontend;
  isLoading: boolean = false;
  private _reports = new BehaviorSubject<RecoveryPlan[]>([]);
  
  constructor(private http: HttpClient,
    private adapter: DataMiningAdapter,
    private dialog: MatDialog,
    private utils: MyUtils,
    private applicationService: ApplicationService,) {

  }

  getAll(filter: IPayload) {
    let payload: IPayload = Object.assign({}, filter || {});
    if (payload) {
      payload.question = {
        limit: "",
        locale: this.applicationService.languageSelectValue,
        orderByScore: "",
        questionCode: "stellantis-sales_nPS_Dealer",
        section: this.applicationService.navBarSelectValue?.idItem,
        tab: this.applicationService.navBarSelectValue.idItem,
      };
    }
    this.isLoading = true;
    return this.http.post(this.endpoint + "v4/getRecoveryPlanList", payload).pipe(
      map((res) => this.adapter.mapCollection(res)),
      tap((res) => this._reports.next(res.items)),
      tap((_) => (this.isLoading = false)),
      catchError((err) => {
        this.isLoading = false;
        return of(this.adapter.mapCollection());
      })
    );
  }

  downloadExcel(el: RecoveryPlan) {
    let payload = cloneDeep(this.utils.settingsPayloadValue.payload);
    payload.question.section = this.applicationService.navBarSelectValue.idItem;
    payload.question.locale = this.applicationService.languageSelectValue;

    return this.http
      .post(this.endpoint + `v4/exportRecoveryPlan`, payload, {
        responseType: "blob",
      })
      .pipe(
        tap((data) => {
          const file = new Blob([data], { type: "text/csv;charset=utf-8" });
          const fileURL = URL.createObjectURL(file);
          const a = document.createElement("a");
          a.href = fileURL;
          a.download = "exportDataMining" + ".xlsx";
          a.target = "_blank";
          a.click();
        })
      );
  }
  genTableConfig() {
    return genDataMiningTableConfig(this);
  }

  createPlan(data: DataMiningDialogData) {
    return this._showRecoveryPlanDialog(data).pipe(
      switchMap((formData) => (formData ? this._sendCreatePlanRequest(data, formData) : of(null)))
    );
  }

  private _showRecoveryPlanDialog(data: DataMiningDialogData) {
    return this.dialog
      .open<
        CecRecoveryPlanDialogComponent, DataMiningDialogData>(CecRecoveryPlanDialogComponent, {
        data: data,
        maxHeight: "80vh",
      })
      .afterClosed();
  }

  get(filter: Payload) {
    return this.http
      .post<any>(
        this.endpoint + "v4/getRecoveryPlanList",
        filter
      )
      .pipe(
        map((res) => res),
        catchError((err) => {
          return of({} as any);
        })
      );
  }

  getRecoveryPlanDomain() {
    let locale = this.applicationService.languageSelectValue;
    return this.http
        .get<any>(
            this.endpoint + `v1/getRecoveryPlanDomain?locale=${locale}`,
        )
        .pipe(
            map((res) => res),
            catchError((err) => {
              return of({} as any);
            })
        );
  }

  private _sendEditPlanRequest(el: RecoveryPlan, form: any) {

    let editedplan = this.adapter.generateEditBody(el, form);

    let b64filePromise = form.file ? this.getBase64(form.file) : of(undefined);
    let b64file$: Observable<string | undefined> = from(b64filePromise);
    return b64file$.pipe(
      map((b64file) => ({ ...editedplan, file: b64file, type: form.file?.type })),
      switchMap((body) => this.http.post(this.endpoint + "v4/updateRecoveryPlan", body))
    );
  }

  private _sendCreatePlanRequest(data: DataMiningDialogData, form: any) {

    let newPlan = this.adapter.generateCreateBody(
      data.surveyId,
      data.brand?? '',
      data.completedDate,
      form
    );

    let b64filePromise = form.file ? this.getBase64(form.file) : of(undefined);
    let b64file$: Observable<string | undefined> = from(b64filePromise);
    return b64file$.pipe(
      map((b64file) => ({ ...newPlan, file: b64file, type: form.file?.type })),
      switchMap((body) => this.http.post(this.endpoint + "v1/addRecoveryPlan", body))
    );
  }

  private getBase64(file: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        let b64 = reader.result as string;
        let ix = b64.indexOf(";base64,");
        if (ix >= 0) {
          let substr = b64.substring(ix);
          let data = substr.replace(";base64,", "");
          return resolve(data);
        } else {
          resolve(reader.result as string);
        }
      };
      reader.onerror = (error) => reject(error);
    });
  }

  getDataMiningDetails(surveyId: string, section: string): Observable<Record<string, string | number>>{
    let locale = this.applicationService.languageSelectValue;

    const params = new HttpParams()
    .append('surveyRequestId', surveyId)
    .append('section', section)
    .append('locale', locale)
    return this.http.get<RecoveryPlan>(`${environment.csibefrontend}v1/getRecoveryPlanForCEC`,{params: params})
    .pipe(
      map( response =>{
        return {
          recoveryPlanId: response.recoveryPlanId,
          description: response.description,
          contactReasonL1: response.contactReasonL1,
          dissatisfacionReason1Level1: response.dissatisfacionReason1Level1,
          dissatisfacionReason2Level1: response.dissatisfacionReason2Level1,
          contactReasonL2: response.contactReasonL2,
          dissatisfacionReason1Level2: response.dissatisfacionReason1Level2,
          dissatisfacionReason2Level2: response.dissatisfacionReason2Level2,
          personEnteringInfo: response.personEnteringInfo,
          dtStart: response.dtStart
        }
      })
    )
  }

  infoPlan(el: any) {
    return this.dialog
      .open(RecoveryPlanInfoComponent, {
        maxHeight: "100vh",
        maxWidth: "100vh",
        data: el,
      })
      .afterClosed()
      .pipe(switchMap((formData) => of(null)));
  }

  closePlan(el: RecoveryPlan, formValue: any) {
    let body = this.adapter.generateCloseBody(el, formValue);
    return this.http.post(this.endpoint + "v4/closeRecoveryPlan", body);
  }

  delete(el: RecoveryPlan) {
    return this.sendDeletePlanRequest(el);
  }

  private sendDeletePlanRequest(el: RecoveryPlan) {
    let body = this.adapter.generateDeleteBody(el);
    return this.http.post(this.endpoint + "v4/deleteRecoveryPlan", body);
  }

  editPlan(el: RecoveryPlan) {
    return this._showRecoveryPlanDialog({
      surveyId: el.surveyRequestId,
      completedDate: el.completedDate,
      outletId: el.outletId,
      isEdit: true,
      title: el.title,
      description: el.description,
      dateEnd: el.dateEnd,
      additionalInformation: el.additionalInformation,
      attach: el.attach,
      brand: el.brandId,
      region: el.regionId,
      recoveryPlanId: el.recoveryPlanId,
      contactReasonL1: el.contactReasonL1,
      contactReasonL2: el.contactReasonL2,
      dissatisfacionReason1Level1: el.dissatisfacionReason1Level1,
      dissatisfacionReason1Level2: el.dissatisfacionReason1Level2,
      dissatisfacionReason2Level1: el.dissatisfacionReason2Level1,
      dissatisfacionReason2Level2: el.dissatisfacionReason2Level2,
      personEnteringInfo: el.personEnteringInfo,
      closed: el.status === "Closed"
    }).pipe(switchMap((data) => (data ? this._sendEditPlanRequest(el, data).pipe(
      switchMap((r) => {

        let payload = cloneDeep(this.utils.settingsPayloadValue.payload);
        payload.question.section = this.applicationService.navBarSelectValue.idItem;
        payload.question.locale = this.applicationService.languageSelectValue;
        return this.get(payload);
      }),

      catchError((err) => {


        return of([]);
      })
    ) : of(null))));
  }

}
